import { useMutation, useQuery } from "@tanstack/react-query";
import { Store } from "sunflower-antd";
import { httpClient, ROOT_URL } from "../App";

type DetailBank = {
  country: string;
  deleted: boolean;
  id: number;
  intermediaryAddress: string;
  intermediaryBank: string;
  intermediarySwift: string;
  legalAddress: string;
  name: string;
  phoneNumber1: string;
  phoneNumber2: string;
  swift: string;
  tin: string;
  userId: number;
};

export const useGetUserBank = (params: {
  start: number;
  end: number;
  name?: string;
}) => {
  return useQuery<{ data: [] }>(
    [`/api/v1/banks/users`, params.start, params.end, params.name],
    async () => getUserBank(params)
  );
};

export function getUserBank({
  start,
  end,
  name,
}: {
  start: number;
  end: number;
  name?: string;
}) {
  const params = new URLSearchParams();
  params.append("_start", String(start));
  params.append("_end", String(end));
  if (name) params.append("_like", name);
  return httpClient.get(`${ROOT_URL}/api/v1/banks/users?${params.toString()}`);
}

export const useGetSystemBank = (params: {
  start: number;
  end: number;
  name?: string;
}) => {
  return useQuery<{ data: [] }>(
    [`/api/v1/banks/system`, params.start, params.end, params.name],
    async () => getSystemBank(params)
  );
};

export function getSystemBank({
  start,
  end,
  name,
}: {
  start: number;
  end: number;
  name?: string;
}) {
  const params = new URLSearchParams();
  params.append("_start", String(start));
  params.append("_end", String(end));
  if (name) params.append("_like", name);
  return httpClient.get(`${ROOT_URL}/api/v1/banks/system?${params.toString()}`);
}

export const useCreateBank = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: createBank,
  });
};

export function createBank(bank: Store) {
  return httpClient.post(`${ROOT_URL}/api/v1/banks/create`, bank);
}

export const useUpdateBank = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateBank,
  });
};

export function updateBank(bank: Store) {
  return httpClient.patch(`${ROOT_URL}/api/v1/banks`, bank);
}

export const useGetBankManagement = (params: {
  start: number;
  end: number;
  name?: string;
  country?: string;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/banks/get-list`,
      params.start,
      params.end,
      params.name,
      params.country,
    ],
    async () => getBankManagement(params)
  );
};

export function getBankManagement({
  start,
  end,
  name,
  country,
}: {
  start: number;
  end: number;
  name?: string;
  country?: string;
}) {
  const params = new URLSearchParams();
  params.append("_start", String(start));
  params.append("_end", String(end));
  if (name) params.append("_like", name);
  if (country) params.append("_country", country);
  return httpClient.get(
    `${ROOT_URL}/api/v1/banks/get-list?${params.toString()}`
  );
}

export const useGetDetailBank = (id: number | null) => {
  return useQuery<{ data: DetailBank }>(
    [`/api/v1/banks/:id`, id],
    async () => getDetailBank(id),
    {
      enabled: !!id,
    }
  );
};

export function getDetailBank(id: number | null) {
  return httpClient.get(`${ROOT_URL}/api/v1/banks/${id}`);
}

export const useDeleteBank = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: deleteBank,
  });
};

export function deleteBank(id: number | null) {
  return httpClient.delete(`${ROOT_URL}/api/v1/banks/${id}`);
}
